import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CommentThread from '../CommentThread';
import { ScrollArea } from "@/components/ui/scroll-area";
import { NoteComment } from '@/types/note';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Capacitor } from '@capacitor/core';

interface CommentDialogProps {
  showDialog: boolean;
  onOpenChange: (open: boolean) => void;
  selectedText: { start: number; end: number; text: string; } | null;
  comments: NoteComment[];
  selectedCommentId?: string | null;
  onAddComment: (text: string, replyTo?: string) => void;
  onDeleteComment?: (commentId: string) => void;
  onSelectTextInEditor?: (start: number, end: number) => void;
}

const CommentDialog: React.FC<CommentDialogProps> = ({
  showDialog,
  onOpenChange,
  selectedText,
  comments,
  selectedCommentId,
  onAddComment,
  onDeleteComment,
  onSelectTextInEditor,
}) => {
  const [newComment, setNewComment] = useState('');
  const isAndroid = Capacitor.getPlatform() === 'android';
  const selectedCommentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedCommentId && selectedCommentRef.current) {
      selectedCommentRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, [selectedCommentId, showDialog]);

  const handleAddComment = () => {
    if (newComment.trim()) {
      onAddComment(newComment);
      setNewComment('');
    }
  };

  return (
    <Dialog open={showDialog} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] sm:w-[85vw] md:w-[75vw] lg:w-[60vw] max-w-2xl mx-auto h-[80vh] sm:h-[70vh] flex flex-col p-0">
        <DialogHeader className="p-4 border-b border-border sticky top-0 bg-background text-foreground z-10">
          <DialogTitle>Comments</DialogTitle>
          <div className="text-sm text-muted-foreground mt-2">
            💡 Tip: Select text in the editor first, then click the comment icon to add comments to specific parts of your note.
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-hidden p-2 space-y-2">
          <ScrollArea className="h-[calc(100%-2rem)] w-full rounded-md p-2">
            {comments.map((comment) => (
              <div 
                key={comment.id} 
                ref={selectedCommentId === comment.id ? selectedCommentRef : null}
                className={`mb-4 p-2 rounded-lg transition-colors ${
                  selectedCommentId === comment.id 
                    ? 'bg-accent border-2 border-primary' 
                    : 'bg-muted'
                }`}
              >
                <div className="text-sm text-muted-foreground mb-2">Selected text:</div>
                <div 
                  className="p-2 rounded border border-border mb-2 break-words bg-card text-foreground cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => comment.selection && onSelectTextInEditor?.(comment.selection.start, comment.selection.end)}
                  title="Click to select this text in the editor"
                >
                  {comment.selection?.text}
                </div>
                <CommentThread
                  comments={[comment]}
                  onAddComment={onAddComment}
                  onDeleteComment={onDeleteComment}
                />
              </div>
            ))}
          </ScrollArea>
          {selectedText && (
            <div className="sticky bottom-0 bg-background text-foreground p-4 border-t border-border">
              <div className="text-sm text-muted-foreground mb-2">New selection:</div>
              <div className="p-2 rounded border border-border mb-2 break-words max-h-[100px] overflow-y-auto bg-muted text-foreground">
                {selectedText.text}
              </div>
              <div className="space-y-2">
                <Textarea
                  placeholder="Add your comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="w-full min-h-[80px] max-h-[120px] p-2 border border-border rounded resize-none bg-background text-foreground placeholder:text-muted-foreground"
                />
                <Button 
                  onClick={handleAddComment}
                  className="w-full"
                  variant="default"
                >
                  Add Comment
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommentDialog;